import Image from 'next/image'
import Link from 'next/link'
import {
  Clock,
  Languages,
  AudioLines,
  Captions,
  Accessibility,
  type LucideIcon,
} from 'lucide-react'

import { difficultyLabels, type AccessibilityFeatures, type Course } from '@/lib/courses/types'

interface CourseCardProps {
  course: Course
}

interface A11yChip {
  key: keyof AccessibilityFeatures
  Icon: LucideIcon
  label: string
}

/**
 * Oznake pristupačnosti kursa. Tekst je vidljiv, ne sr-only i ne u `title`:
 * `title` se ne pokazuje ni na tastaturi ni na dodiru, pa je informacija bila
 * nedostupna svima osim mišu i čitaču ekrana.
 *
 * AudioLines (zvučni talasi), ne VolumeX (prekriženi zvučnik) — prekrižena
 * ikona znači „nema zvuka", tačno suprotno od onoga što oznaka tvrdi.
 */
const A11Y_CHIPS: readonly A11yChip[] = [
  { key: 'signLanguage', Icon: Languages, label: 'Znakovni jezik' },
  { key: 'captions', Icon: Captions, label: 'Titlovi' },
  { key: 'audioDescription', Icon: AudioLines, label: 'Audio opisi' },
  { key: 'easyRead', Icon: Accessibility, label: 'Easy Read' },
]

export function CourseCard({ course }: CourseCardProps) {
  return (
    <Link
      href={`/kursevi/${course.slug}`}
      className="card-elevated bg-background focus-visible:ring-ring group flex flex-col gap-4 rounded-xl border p-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
    >
      {/* 16:9 vrh kartice. Fallback zauzima isti okvir kao slika, pa se kartice
          sa slikom i bez nje ne razlikuju po visini — mreža ostaje poravnata. */}
      <div className="relative aspect-video w-full overflow-hidden rounded-lg">
        {course.coverImageUrl ? (
          <Image
            src={course.coverImageUrl}
            alt={course.coverImageAlt ?? ''}
            fill
            sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
            className="object-cover"
          />
        ) : (
          <div className="emoji-bubble h-full w-full rounded-none" aria-hidden="true">
            <span className="text-5xl leading-none">{course.coverEmoji}</span>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        {/* Badge težine je ostao bez para otkad je ikona 56×56 otišla u 16:9
            zaglavlje; stoji uz kategoriju umjesto u praznom redu za sebe. */}
        <div className="flex items-center justify-between gap-3">
          <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
            {course.categoryLabel}
          </p>
          <span className="bg-primary-light text-primary flex-shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium">
            {difficultyLabels[course.difficulty]}
          </span>
        </div>
        <h3 className="group-hover:text-primary text-lg font-semibold transition-colors">
          {course.title}
        </h3>
        <p className="text-muted-foreground text-sm leading-relaxed">{course.summary}</p>
      </div>

      {/* gap-y drži razmak kad se chipovi prelome (zoom 200%), a min-w-0 na
          chipu dozvoljava da se dugačka oznaka prelomi umjesto da iscuri. */}
      <div className="text-muted-foreground mt-auto flex flex-wrap items-center gap-x-3 gap-y-2 border-t pt-3 text-xs">
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <Clock aria-hidden="true" className="h-3.5 w-3.5 flex-shrink-0" />
          {course.estimatedMinutes} min
        </span>
        {A11Y_CHIPS.filter(({ key }) => course.accessibilityFeatures[key]).map(
          ({ key, Icon, label }) => (
            <span
              key={key}
              className="inline-flex min-w-0 items-center gap-1.5 rounded-full border px-2 py-0.5"
            >
              <Icon aria-hidden="true" className="h-3.5 w-3.5 flex-shrink-0" />
              {label}
            </span>
          ),
        )}
      </div>
    </Link>
  )
}
